import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useCookies } from "react-cookie";

import { Button } from "../../../components/ui/button";
import { toast } from "../../../components/toast";
import {
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  Dialog,
} from "../../../components/ui/dialog";

import { useLoginUser } from "../../../context/auth";
import { formatDate } from "../../../lib/helper";
import { useUsecases } from "../../../store";

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TrialDialog({ open, onOpenChange }: DialogProps) {
  const { loginUser } = useLoginUser();
  const { authUsecase } = useUsecases();
  const { t } = useTranslation();

  const [, setCookie] = useCookies(["trial"]);

  const trial = loginUser.trial.expire?.toString();
  const trialExpire = trial ? formatDate(trial) : "N/A";

  const { mutate: keepTrial } = useMutation({
    mutationFn: () => {
      return authUsecase.keepTrial();
    },
    onSuccess: () => {
      const expire = new Date();
      expire.setDate(expire.getDate() + 1);
      setCookie("trial", expire, {
        expires: expire,
        path: "/",
        secure: true,
      });
      toast({
        type: "success",
        message: t("auth-trial-toast-success"),
      });
      
      onOpenChange(false);
    },
    onError: () => {
      toast({
        type: "error",
        message: t("auth-trial-toast-error"),
      });
      onOpenChange(false);
    },
  });

  const { mutate: endTrial } = useMutation({
    mutationFn: () => {
      return authUsecase.skipTrial();
    },
    onSuccess: () => {
      const expire = new Date();
      expire.setDate(expire.getDate() + 1);
      setCookie("trial", expire, {
        expires: expire,
        path: "/",
        secure: true,
      });
      toast({
        type: "success",
        message: t("auth-trial-toast-success"),
      });
      onOpenChange(false);
    },
    onError: () => {
      toast({
        type: "error",
        message: t("auth-trial-toast-error"),
      });
      onOpenChange(false);
    },
  });

  const skipTrial = () => {
    const expire = new Date();
    expire.setDate(expire.getDate() + 1);
    setCookie("trial", expire, {
      expires: expire,
      path: "/",
      secure: true,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[460px] rounded-xl border border-gray-100 dark:border-white/10">
        <div className="flex flex-col grow-0 gap-y-5">
          <DialogHeader>
            <DialogTitle>{t("auth-trial-title")}</DialogTitle>
            <div className="flex flex-col w-full py-5">
              <div className="w-full rounded-md bg-black/5 dark:bg-black/50 p-6 overflow-x-auto">
                <code>
                  <pre className="grid gap-1 text-sm text-black dark:text-white [&_span]:h-4">
                    {trialExpire}
                  </pre>
                </code>
              </div>
              <p className="pt-5">
                {t("auth-trial-message-1")}
                <br />
                {t("auth-trial-message-2")}
              </p>
            </div>
          </DialogHeader>
          <DialogFooter className="gap-x-2">
            <Button
              className="flex flex-row h-8 w-24 px-3 items-center rounded-lg text-xs text-white bg-[#ef8262] hover:bg-[#ef8262]/80"
              onClick={() => endTrial()}
              variant="headless"
              size="headless"
              type="button"
            >
              {t("auth-trial-submit-1")}
            </Button>
            <Button
              className="flex flex-row h-8 w-24 px-3 items-center rounded-lg text-xs text-white bg-[#0195c5] hover:bg-[#0195c5]/80"
              onClick={() => skipTrial()}
              variant="headless"
              size="headless"
              type="button"
            >
              {t("auth-trial-submit-2")}
            </Button>
            <Button
              className="flex flex-row h-8 w-24 px-3 items-center rounded-lg text-xs text-white bg-[#0195c5] hover:bg-[#0195c5]/80"
              onClick={() => keepTrial()}
              variant="headless"
              size="headless"
              type="button"
            >
              {t("auth-trial-submit-3")}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
